/**
 * Site Banners
 *
 * These are banners that are displayed at the top of the site potentially on
 * every page load and as the user navigates through the site.
 */
import * as React from "react";

import {RequestInfo} from "@khan/shared-core";

import {getPreferredLocale} from "./banners/i18n-suggest/i18n-suggest-utils.ts";
import {getPendingEmailToVerify} from "./banners/verify-email/verify-email-utils.ts";
import type {BannerConfig, BannerProps} from "./types.ts";

// All of the site banners to potentially display. They should be listed in
// priority order, the first banner to return `true` from `shouldRender` will
// display.
const banners: Array<BannerConfig> = [
    /**
     * Banner shown to user if they are impersonating another user. Reminds
     * them to be careful not to modify user data.
     */
    {
        name: "impersonation",

        shouldRender({profile}) {
            return !!profile && profile.get("actorIsImpersonatingUser");
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/impersonation/impersonation.tsx"),
        ),
    },

    /*
     * Remind under age gate students to have their parents approve their
     * accounts.
     */
    {
        name: "under-age-gate-reminder",

        shouldRender({profile}) {
            const underAgeGate = profile?.get("underAgeGate");

            if (underAgeGate && !underAgeGate.approvalGivenAt) {
                return true;
            }

            return false;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () =>
                import(
                    "./banners/under-age-gate-reminder/under-age-gate-reminder.tsx"
                ),
        ),
    },

    /**
     * Banner shown to user if they much accept the Terms of Service and
     * Privacy Policy.
     *
     * This shows a modal requiring users to accept the terms before continuing
     * to use the site.
     */
    {
        name: "accept-tos",

        shouldRender({profile}) {
            // We only want it to display if the user hasn't accepted the
            // Terms of Service already and they're not a phantom user.
            return (
                !!profile &&
                !profile.get("isPhantom") &&
                !profile.get("tosAccepted")
            );
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/accept-tos/accept-tos.tsx"),
        ),
    },

    /**
     * A modal that requires users to verify their birthdate.
     *
     * This shows a modal requiring users to specify how old they are before
     * they can continue using the site.
     */
    {
        name: "age-check",

        shouldRender({profile}) {
            // We only want it to display if the user should see the age check
            return !!profile?.get("shouldShowAgeCheck");
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/age-check/age-check.tsx"),
        ),
    },

    /**
     * Banner shown to user if there is a site outage.
     *
     * This shows a site banner (at fairly high priority, above everything
     * except legal stuff) warning users that there may be serious issues with
     * the site, and directing them to our status page for more information.
     */
    {
        name: "site-outage",

        shouldRender({
            flags: {
                siteOutageBanner: {severity},
            },
        }) {
            // We only want it to display if there is a severity to the banner
            return !!severity;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/site-outage/site-outage.tsx"),
        ),
    },

    /**
     * Banner shown to user whenever their registration email bounces.
     */
    {
        name: "bounced-email",

        shouldRender({profile}) {
            const signupDataIfUnverified = profile?.get(
                "signupDataIfUnverified",
            );
            return !!signupDataIfUnverified?.emailBounced;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/bounced-email/bounced-email.tsx"),
        ),
    },

    /**
     * Site banner shown to user if waiting for them to verify their email.
     *
     * This banner is shown in 2 scenarios:
     *  1. After a phantom user begins the email signup process and then comes
     *      back to the site. This is represented by an UnverifiedUser entity.
     *  2. After a regular user begins the "add linked email" process from the
     *      /settings page. This is represented by an UnverifiedAuthEmail
     *      entity.
     * Both scenarios offer buttons to "resend email" and "change email", but
     * these buttons perform slightly different actions based on which scenario
     * the banner is shown in.
     */
    {
        name: "verify-email",

        shouldRender({profile}) {
            return !!getPendingEmailToVerify(profile);
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/verify-email/verify-email.tsx"),
        ),
    },

    /**
     * Suggest a different version of the site if the user has a different
     * preferred language (as specifiec by their browser) from the one that
     * they're currently viewing the site of.
     */
    {
        name: "i18n-suggest",

        shouldRender() {
            // getPreferredLocale returns an empty string if there is no
            // locale to switch to or if they're already on their preferred
            // locale.
            return getPreferredLocale() !== "";
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/i18n-suggest/i18n-suggest.tsx"),
        ),
    },

    /**
     * Recommend that the user login or signup if they have any unsaved
     * progress, such as a partially-completed exercise, video views, etc.
     * NOTE: we hide this banner on login or sign up pages
     */
    {
        name: "phantom",

        shouldRender({profile, location}) {
            // Don't show if we're on a login or sign up page.
            if (location.pathname.match(/login|signup/)) {
                return false;
            }

            return !!(profile && profile.get("isPhantom"));
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/phantom/phantom.tsx"),
        ),
    },

    /**
     * Shown to users who have full accounts but have not added an email.
     *
     * Usually, this means a "child account" (which is created without an email
     * address) has "grown up" (meaning they are above their age gate or, more
     * technically, that `profile.get("isChildAccount")` returns False) and they
     * have not input an email address on the settings page yet.
     *
     * A full account may also be missing an email address if we did not or were
     * unable to collect one at signup time.  This is expected to be rare and only
     * occur for legacy accounts.
     */
    {
        name: "link-email",

        shouldRender({profile}) {
            if (!profile) {
                return false;
            }

            if (profile.get("isPhantom") || profile.get("isChildAccount")) {
                return false;
            }

            if (profile.get("authEmails").length > 0) {
                return false;
            }

            if (profile.get("pendingEmailVerifications").length === 0) {
                return true;
            }

            return false;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/link-email/link-email.tsx"),
        ),
    },

    /**
     * This shows a site banner informing users of updates to our terms of service.
     *
     * This will be set to not render if legal pages have not been updated in
     * the past 30 days.
     */
    {
        name: "legal-updates",

        shouldRender(): boolean {
            return false;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () => import("./banners/legal-updates/legal-updates.tsx"),
        ),
        autoDismiss: true,
    },

    /**
     * This shows a site banner informing users of updates to our privacy policy.
     */
    {
        name: "privacy-policy-updates",

        shouldRender(): boolean {
            return false;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () =>
                import(
                    "./banners/privacy-policy-updates/privacy-policy-updates.tsx"
                ),
        ),
        autoDismiss: true,
    },

    /**
     * Shown to teachers about students using shortcuts to complete activities.
     */
    {
        name: "cheating-banner-for-teachers",

        shouldRender({flags: {showCheatingBannerForTeachers}}) {
            return showCheatingBannerForTeachers;
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () =>
                import(
                    "./banners/cheating-banner-for-teachers/cheating-banner-for-teachers.tsx"
                ),
        ),
    },

    /**
     * Shown to grassroots parents and all logged-out users in the US
     * promoting the new activity report
     */
    {
        name: "parent-dashboard-promo",

        // Grassroots parents logic is performed within the banner component
        shouldRender(): boolean {
            return RequestInfo.remoteCountry === "US";
        },

        component: React.lazy<React.ComponentType<BannerProps>>(
            () =>
                import(
                    "./banners/parent-dashboard-promo/parent-dashboard-promo.tsx"
                ),
        ),
    },
];

export const getBanners = (): Array<BannerConfig> => banners;
